import type { OnboardingDataItemType } from "@memorang/constants/config/schema";
import { useTranslation } from "react-i18next";
import { List, Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import { useOnboarding } from "./OnboardingProvider";
import { OnboardingBox } from "./components/OnboardingBox";
import { resolveI18n } from "./helpers";

type Props = {
	title: string;
	description?: string;
	data?: OnboardingDataItemType[];
};

type RankItemProps = {
	item: OnboardingDataItemType;
	isSelected: boolean;
	locale: string;
	onPress: () => void;
};

const RankItem = ({ item, isSelected, locale, onPress }: RankItemProps) => {
	const theme = useAppTheme();
	const title = resolveI18n(item.titlei18n, locale);
	const description = resolveI18n(item.descriptioni18n, locale);

	return (
		<List.Item
			onPress={onPress}
			style={{
				borderWidth: 1,
				borderColor: isSelected
					? theme.colors.secondary
					: theme.colors.surfaceVariant,
				borderRadius: theme.roundness * 2,
				backgroundColor: isSelected
					? theme.colors.elevation.level1
					: theme.colors.surface,
				padding: 16,
			}}
			contentStyle={{ gap: 1 }}
			left={() => (
				<Box
					style={{
						width: 36,
						height: 36,
						borderRadius: 99,
						backgroundColor: isSelected
							? theme.colors.elevation.level5
							: theme.colors.background,
						justifyContent: "center",
						alignItems: "center",
						alignSelf: "center",
					}}
				>
					<Text
						variant="labelMedium"
						style={{
							fontWeight: "700",
							color: isSelected
								? theme.colors.secondary
								: theme.colors.inverseSurface,
						}}
					>
						{item.iconString ?? ""}
					</Text>
				</Box>
			)}
			title={title}
			titleStyle={{ fontWeight: "400" }}
			description={description}
			descriptionStyle={{ color: theme.colors.onSurfaceVariant }}
		/>
	);
};

export const OnboardingRankScreen = ({
	title,
	description,
	data = [],
}: Props) => {
	const { selectedRankId, onSelectRank } = useOnboarding();
	const theme = useAppTheme();
	const { t, i18n } = useTranslation();
	const locale = i18n.language;

	return (
		<OnboardingBox>
			<Box gap={14}>
				<Text variant="titleLarge">{title}</Text>
				{description ? (
					<Text
						variant="bodyMedium"
						style={{ color: theme.colors.onSurfaceVariant }}
					>
						{description}
					</Text>
				) : null}
			</Box>
			<Box gap={8}>
				<Text
					variant="labelSmall"
					style={{ color: theme.colors.onSurfaceVariant }}
				>
					{t("onboarding.steps.rank.prompt", {
						defaultValue: "Which statement sounds most like you?",
					})}
				</Text>
				<Box gap={8}>
					{data.map((item) => (
						<RankItem
							key={
								item.type ??
								item.iconString ??
								resolveI18n(item.titlei18n, "en-US") ??
								""
							}
							item={item}
							locale={locale}
							isSelected={
								selectedRankId ===
								(item.type ??
									item.iconString ??
									resolveI18n(item.titlei18n, "en-US") ??
									"")
							}
							onPress={() =>
								onSelectRank(
									item.type ??
										item.iconString ??
										resolveI18n(item.titlei18n, "en-US") ??
										"",
								)
							}
						/>
					))}
				</Box>
			</Box>
			<Box
				style={{
					borderRadius: theme.roundness * 2.5,
					borderWidth: 1,
					borderColor: theme.colors.surfaceVariant,
					backgroundColor: theme.colors.background,
					paddingVertical: 13,
					paddingHorizontal: 15,
				}}
			>
				<Text variant="bodySmall" style={{ color: theme.colors.onSurface }}>
					{t("onboarding.steps.rank.hint", {
						defaultValue:
							"We'll use your answer to estimate your starting level",
					})}
				</Text>
			</Box>
		</OnboardingBox>
	);
};
